import { useEffect, useState } from "react";
import { api, errText } from "../../lib/api";
import { Truck, Trash2, Plus, Copy, Check, Pencil, Pause, Play, X } from "lucide-react";

export function DeliveryModeCard({ mode, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // Read the plan block from /auth/me so we can lock the Network option when the
  // shop's plan doesn't include Fleurinelle-network delivery (Éclat Free today).
  const [networkAllowed, setNetworkAllowed] = useState(true);
  const [planName, setPlanName] = useState("");
  useEffect(() => {
    api.get("/auth/me").then((r) => {
      const feats = r.data?.plan?.features || {};
      setNetworkAllowed(feats.use_network_delivery !== false);
      setPlanName(r.data?.plan?.name || "");
    }).catch(() => {});
  }, []);
  const set = async (m) => {
    if (m === mode) return;
    if (m === "network" && !networkAllowed) {
      setErr(`Le réseau Fleurinelle n'est pas inclus dans ${planName || "votre plan"}. Passez à un plan supérieur pour l'activer.`);
      return;
    }
    setBusy(true); setErr("");
    try { await api.put("/florist/delivery-mode", { mode: m }); onChanged(m); }
    catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };
  const Opt = ({ value, title, desc, locked }) => (
    <button type="button" data-testid={`delivery-mode-${value}`}
            disabled={busy || locked}
            onClick={() => set(value)}
            className={`text-left p-4 border rounded-sm w-full transition-colors relative
                        ${locked ? "border-brass-light bg-plaster-deep/50 opacity-60 cursor-not-allowed" :
                          mode === value ? "border-ink bg-plaster-deep" : "border-brass-light hover:border-brass"}`}>
      <p className="text-sm text-ink font-medium flex items-center gap-2">
        {mode === value && !locked ? <Check size={15} className="text-brass" /> : null}
        {title}
        {locked ? <span className="text-[10px] uppercase tracking-wider bg-ink text-plaster px-1.5 py-0.5">Plan requis</span> : null}
      </p>
      <p className="text-xs text-muted mt-1">{desc}</p>
      {locked ? (
        <p className="text-xs text-brass mt-2" data-testid={`delivery-mode-${value}-upgrade`}>
          Passez à Éclat Studio ou Prestige pour débloquer.
        </p>
      ) : null}
    </button>
  );
  return (
    <div className="card p-5 space-y-3" data-testid="delivery-mode-card">
      <div className="flex items-center gap-2"><Truck size={16} className="text-brass" /><h3 className="smallcaps">Mode de livraison</h3></div>
      <p className="text-xs text-muted">Fleurinelle fixe les frais de livraison. Choisissez qui livre vos commandes.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <Opt value="network" title="Réseau Fleurinelle" locked={!networkAllowed}
             desc="Les livreurs Fleurinelle s'occupent de tout. Les frais de livraison reviennent à la plateforme." />
        <Opt value="self" title="Ma propre équipe"
             desc="Vos livreurs livrent. Vous gardez 100 % des frais de livraison et fixez vos propres prix par zone." />
      </div>
      {err ? <p className="text-danger text-sm" data-testid="delivery-mode-error">{err}</p> : null}
    </div>
  );
}

function DriverRow({ driver, onSaved, onRemoved }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: driver.name, phone: driver.phone || "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const suspended = driver.status === "suspended";

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.patch(`/florist/drivers/${driver.id}`, {
        name: form.name.trim(), phone: form.phone.trim(),
      });
      onSaved(data.driver);
      setEditing(false);
    } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };

  const toggleStatus = async () => {
    setBusy(true); setErr("");
    try {
      const next = suspended ? "active" : "suspended";
      const { data } = await api.patch(`/florist/drivers/${driver.id}`, { status: next });
      onSaved(data.driver);
    } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Supprimer ${driver.name} ? Les commandes en cours qui lui étaient assignées seront rendues à la boutique.`)) return;
    setBusy(true); setErr("");
    try {
      await api.delete(`/florist/drivers/${driver.id}`);
      onRemoved(driver.id);
    } catch (e) { setErr(errText(e)); setBusy(false); }
  };

  return (
    <div className={`card p-3 ${suspended ? "opacity-60" : ""}`} data-testid={`driver-row-${driver.id}`}>
      {editing ? (
        <div className="space-y-2">
          <div className="grid sm:grid-cols-2 gap-2">
            <input className="field" data-testid={`driver-edit-name-${driver.id}`} placeholder="Nom"
                   value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input className="field" data-testid={`driver-edit-phone-${driver.id}`} placeholder="Téléphone"
                   value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          {err ? <p className="text-danger text-xs">{err}</p> : null}
          <div className="flex gap-2 justify-end">
            <button className="btn-ghost text-xs" data-testid={`driver-edit-cancel-${driver.id}`}
                    onClick={() => { setEditing(false); setErr(""); setForm({ name: driver.name, phone: driver.phone || "" }); }}>
              <X size={13} className="inline me-1" /> Annuler
            </button>
            <button className="btn-primary text-xs" data-testid={`driver-edit-save-${driver.id}`}
                    disabled={busy || !form.name.trim() || form.name.trim().length < 2} onClick={save}>
              <Check size={13} className="inline me-1" /> Enregistrer
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm text-ink truncate">
              {driver.name}
              {suspended ? <span className="ms-2 text-xs text-danger" data-testid={`driver-status-${driver.id}`}>· Suspendu</span> : null}
            </p>
            <p className="text-xs text-muted truncate">{driver.email} · {driver.phone || "—"}</p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button className="p-2 text-muted hover:text-ink" title="Modifier"
                    data-testid={`driver-edit-${driver.id}`} disabled={busy} onClick={() => setEditing(true)}>
              <Pencil size={15} />
            </button>
            <button className={`p-2 ${suspended ? "text-brass hover:text-ink" : "text-muted hover:text-ink"}`}
                    title={suspended ? "Réactiver" : "Suspendre"}
                    data-testid={`driver-toggle-${driver.id}`} disabled={busy} onClick={toggleStatus}>
              {suspended ? <Play size={15} /> : <Pause size={15} />}
            </button>
            <button className="p-2 text-danger" title="Supprimer"
                    data-testid={`driver-remove-${driver.id}`} disabled={busy} onClick={remove}>
              <Trash2 size={15} />
            </button>
          </div>
        </div>
      )}
      {!editing && err ? <p className="text-danger text-xs mt-2">{err}</p> : null}
    </div>
  );
}

export default function FloristDrivers() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = () => api.get("/florist/drivers").then((r) => setData(r.data)).catch(() => setData({ drivers: [] }));
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault(); setBusy(true); setErr(""); setCreated(null);
    try { const { data } = await api.post("/florist/drivers", { ...form, zone_ids: [] }); setCreated(data.driver); setForm({ name: "", email: "", phone: "" }); load(); }
    catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };

  const replaceDriver = (updated) => {
    setData((d) => ({ ...d, drivers: (d?.drivers || []).map((x) => x.id === updated.id ? { ...x, ...updated } : x) }));
  };
  const removeDriver = (id) => {
    setData((d) => ({ ...d, drivers: (d?.drivers || []).filter((x) => x.id !== id) }));
  };

  return (
    <div className="space-y-4" data-testid="florist-drivers">
      <div><h2 className="font-display text-2xl text-ink">Mes livreurs</h2>
        <p className="text-xs text-muted">Invitez, modifiez, suspendez ou supprimez vos livreurs. Ils se connectent sur <b>/driver</b> avec leur mot de passe temporaire.</p></div>

      <form onSubmit={invite} className="card p-4 space-y-3" data-testid="driver-invite-form">
        <div className="grid sm:grid-cols-3 gap-3">
          <input required data-testid="invite-name" className="field" placeholder="Nom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input required data-testid="invite-email" className="field" placeholder="E-mail" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input data-testid="invite-phone" className="field" placeholder="Téléphone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </div>
        {err ? <p className="text-danger text-sm" data-testid="invite-error">{err}</p> : null}
        <button disabled={busy} data-testid="invite-submit" className="btn-primary text-sm flex items-center gap-1.5"><Plus size={15} /> Inviter le livreur</button>
      </form>

      {created ? (
        <div className="card p-4 space-y-2 bg-plaster-deep" data-testid="invite-created">
          <p className="text-sm text-ink">Livreur créé : <b>{created.name}</b></p>
          <p className="text-xs text-muted">Envoyez-lui ces accès (l'e-mail part automatiquement dès que l'envoi est configuré) :</p>
          <div className="text-xs text-ink space-y-1">
            <p>Connexion : <b>{created.invite_link}</b></p>
            <p>E-mail : <b>{created.email}</b></p>
            <p className="flex items-center gap-2">Mot de passe : <b>{created.temp_password}</b>
              <button className="text-brass" onClick={() => { navigator.clipboard?.writeText(`${created.invite_link}\n${created.email}\n${created.temp_password}`); setCopied(true); }}>
                {copied ? <Check size={13} /> : <Copy size={13} />}</button></p>
          </div>
        </div>
      ) : null}

      <div className="space-y-2">
        {(data?.drivers || []).length === 0 ? <p className="text-sm text-muted py-6" data-testid="drivers-empty">Aucun livreur pour le moment.</p> : null}
        {(data?.drivers || []).map((d) => (
          <DriverRow key={d.id} driver={d} onSaved={replaceDriver} onRemoved={removeDriver} />
        ))}
      </div>
    </div>
  );
}
